import React from "react";
import { Box, Typography, Stack, Container, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import FeatureCard from "../components/FeatureCard";
import { servicesData } from "../data/vizapData";

const ServicesContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  backgroundColor: "#0D2847",
  padding: "100px 0",
  paddingTop: "120px",
  position: "relative",
  borderRadius: "40px 40px 0px 0px",
  overflow: "hidden",
  marginTop: "-40px",
  [theme.breakpoints.down("md")]: {
    borderRadius: "20px 20px 0px 0px",
    padding: "60px 0",
    paddingTop: "80px",
  },
}));

const ServicesSection = () => {
  return (
    <ServicesContainer>
      {/* Decorative elements */}
      <Box
        component={motion.div}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        sx={{
          position: "absolute",
          top: "20%",
          left: "-5%",
          width: { xs: "300px", md: "600px" },
          height: { xs: "300px", md: "600px" },
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(70, 116, 217, 0.15) 0%, transparent 70%)",
          filter: "blur(80px)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, px: { xs: 3, sm: 4, md: 5, lg: 6 } }}>
        <Stack spacing={{ xs: 4, md: 6 }}>
          {/* Section Header */}
          <Stack
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            spacing={2}
            alignItems="center"
            textAlign="center"
          >
            <Typography
              sx={{
                color: "#CACFD7",
                fontSize: { xs: "32px", md: "44px" },
                fontWeight: 700,
                fontFamily: "'Poppins', sans-serif",
                lineHeight: 1.2,
              }}
            >
              {servicesData.title}
            </Typography>
            <Typography
              sx={{
                color: "#FFFFFF",
                fontSize: { xs: "14px", md: "16px" },
                maxWidth: "700px",
                lineHeight: 1.6,
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 400,
                opacity: 0.85,
                px: { xs: 1, md: 0 },
              }}
            >
              {servicesData.subtitle}
            </Typography>
          </Stack>

          {/* Main Content Grid */}
          <Grid 
            container 
            spacing={0}
            alignItems="stretch"
            sx={{
              width: "100%",
              margin: 0,
            }}
          >
            {/* Left Column - Feature Cards */}
            <Grid 
              item 
              xs={12} 
              md={4} 
              sx={{ 
                display: "flex", 
                flexDirection: "column",
                pr: { xs: 0, md: 2 },
                mb: { xs: 3, md: 0 },
              }}
            >
              <Stack spacing={{ xs: 2.5, md: 3 }} sx={{ width: "100%", height: "100%" }}>
                <FeatureCard {...servicesData.features[0]} />
                <FeatureCard {...servicesData.features[1]} />
              </Stack>
            </Grid>

            {/* Center - Phone Mockup */}
            <Grid 
              item 
              xs={12} 
              md={4}
              sx={{
                px: { xs: 0, md: 2 },
                mb: { xs: 3, md: 0 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: { xs: "flex-start", md: "center" },
                  position: "relative",
                  width: "100%",
                  py: { xs: 2, md: 0 },
                }}
              >
                <Box
                  component={motion.div}
                  animate={{
                    y: [0, -20, 0],
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  sx={{
                    position: "relative",
                    width: { xs: "200px", sm: "220px", md: "280px" },
                    height: { xs: "360px", sm: "400px", md: "560px" },
                    mx: "auto",
                  }}
                >
                  {/* Glow effect */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "150%",
                      height: "150%",
                      background: "radial-gradient(circle, rgba(70, 116, 217, 0.4) 0%, transparent 70%)",
                      filter: "blur(60px)",
                      zIndex: 0,
                    }}
                  />
                  
                  {/* Phone frame */}
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      background: "linear-gradient(145deg, #1a1a2e 0%, #0f0f1e 100%)",
                      borderRadius: { xs: "24px", md: "36px" },
                      border: { xs: "6px solid #2a2a3e", md: "8px solid #2a2a3e" },
                      boxShadow: "0 30px 80px rgba(0, 0, 0, 0.7), inset 0 0 0 1px rgba(255, 255, 255, 0.1)",
                      overflow: "hidden",
                      zIndex: 1,
                    }}
                  >
                    {/* Notch */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "35%",
                        height: { xs: "18px", md: "26px" },
                        background: "#1a1a2e",
                        borderRadius: { xs: "0 0 10px 10px", md: "0 0 16px 16px" },
                        zIndex: 3,
                      }}
                    />

                    {/* Screen content */}
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        background: "#FFFFFF",
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                      }}
                    >
                      {/* Professional lab report header */}
                      <Box
                        sx={{
                          p: { xs: 1, md: 1.5 },
                          pt: { xs: 2.5, md: 3.5 },
                          background: "linear-gradient(180deg, #4674D9 0%, #3567D6 100%)",
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: { xs: 0.5, md: 1 } }}>
                          <Typography sx={{ color: "rgba(255, 255, 255, 0.9)", fontSize: { xs: "8px", md: "10px" }, fontWeight: 600 }}>
                            9:41
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Box sx={{ width: { xs: "10px", md: "12px" }, height: { xs: "7px", md: "9px" }, border: { xs: "1px solid", md: "1.5px solid" }, borderColor: "rgba(255, 255, 255, 0.9)", borderRadius: "2px", position: "relative", opacity: 0 }}>
                              <Box sx={{ position: "absolute", right: { xs: "-1.5px", md: "-2px" }, top: "50%", transform: "translateY(-50%)", width: { xs: "1px", md: "1.5px" }, height: { xs: "2px", md: "3px" }, background: "rgba(255, 255, 255, 0.9)", borderRadius: "0 1px 1px 0" }} />
                            </Box>
                          </Stack>
                        </Stack>
                        <Typography
                          sx={{
                            color: "#FFFFFF",
                            fontSize: { xs: "11px", md: "14px" },
                            fontWeight: 700,
                            fontFamily: "'Poppins', sans-serif",
                            textAlign: "center",
                            mb: { xs: 0.3, md: 0.5 },
                          }}
                        >
                          Lab Report Analysis
                        </Typography>
                        <Typography
                          sx={{
                            color: "rgba(255, 255, 255, 0.7)",
                            fontSize: { xs: "7px", md: "9px" },
                            fontFamily: "'Poppins', sans-serif",
                            textAlign: "center",
                          }}
                        >
                          Patient ID: VIZ-2024-001
                        </Typography>
                      </Box>

                      {/* Test categories with clinical data */}
                      <Box
                        sx={{
                          flex: 1,
                          p: { xs: 1, md: 1.5 },
                          display: "flex",
                          flexDirection: "column",
                          gap: { xs: 0.6, md: 1 },
                          overflowY: "auto",
                        }}
                      >
                        {[
                          { 
                            category: "Hematology",
                            count: "8",
                            key: "WBC 7.2 • RBC 4.8 • Platelets 250K",
                            status: "Normal",
                            color: "#FF6B6B",
                          },
                          { 
                            category: "Biochemistry",
                            count: "12",
                            key: "Glucose 95 • Urea 28 • Creatinine 0.9",
                            status: "Normal",
                            color: "#4ECDC4",
                          },
                          { 
                            category: "Lipid Profile",
                            count: "6",
                            key: "Total Chol. 178 • LDL 102 • HDL 58",
                            status: "Optimal",
                            color: "#95E1D3",
                          },
                          { 
                            category: "Liver Function",
                            count: "9",
                            key: "ALT 28 • AST 32 • Bilirubin 0.8",
                            status: "Normal",
                            color: "#FFD93D",
                          },
                          { 
                            category: "Thyroid Panel",
                            count: "4",
                            key: "TSH 2.1 • T3 1.2 • T4 8.5",
                            status: "Normal",
                            color: "#A78BFA",
                          },
                          { 
                            category: "Vitamins",
                            count: "5",
                            key: "Vit D 38 • B12 450 • Folate 12",
                            status: "Good",
                            color: "#6BCF7F",
                          },
                        ].map((item, index) => (
                          <Box
                            key={index}
                            component={motion.div}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{
                              duration: 0.3,
                              delay: index * 0.08,
                              repeat: Infinity,
                              repeatType: "reverse",
                              repeatDelay: 6,
                            }}
                            sx={{
                              background: "#FFFFFF",
                              borderRadius: { xs: "6px", md: "8px" },
                              p: { xs: 0.7, md: 1 },
                              border: { xs: `1px solid ${item.color}40`, md: `1.5px solid ${item.color}40` },
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                borderColor: `${item.color}80`,
                                transform: "translateX(3px)",
                                boxShadow: `0 2px 8px ${item.color}20`,
                              }
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={{ xs: 0.6, md: 1 }}>
                              <Box flex={1}>
                                <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, md: 0.8 }} sx={{ mb: { xs: 0.3, md: 0.5 } }}>
                                  <Box
                                    sx={{
                                      width: { xs: "4px", md: "5px" },
                                      height: { xs: "4px", md: "5px" },
                                      borderRadius: "50%",
                                      background: item.color,
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      color: "#282D4D",
                                      fontSize: { xs: "8px", md: "10px" },
                                      fontWeight: 700,
                                      fontFamily: "'Poppins', sans-serif",
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {item.category}
                                  </Typography>
                                  <Box
                                    sx={{
                                      px: { xs: 0.4, md: 0.6 },
                                      py: { xs: 0.1, md: 0.2 },
                                      borderRadius: "3px",
                                      background: `${item.color}20`,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        color: item.color,
                                        fontSize: { xs: "6px", md: "7px" },
                                        fontWeight: 700,
                                        fontFamily: "'Poppins', sans-serif",
                                      }}
                                    >
                                      {item.count} tests
                                    </Typography>
                                  </Box>
                                </Stack>
                                <Typography
                                  sx={{
                                    color: "#7F7F7F",
                                    fontSize: { xs: "7px", md: "8px" },
                                    fontWeight: 400,
                                    fontFamily: "'Poppins', sans-serif",
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {item.key}
                                </Typography>
                              </Box>
                              <Typography
                                sx={{
                                  color: item.color,
                                  fontSize: { xs: "6px", md: "7px" },
                                  fontWeight: 700,
                                  fontFamily: "'Poppins', sans-serif",
                                  textTransform: "uppercase",
                                  mt: { xs: 0.2, md: 0.3 },
                                }}
                              >
                                {item.status}
                              </Typography>
                            </Stack>
                          </Box>
                        ))}
                      </Box>

                      {/* Footer with AI insights */}
                      <Box
                        sx={{
                          p: { xs: 1, md: 1.5 },
                          background: "rgba(70, 116, 217, 0.1)",
                          borderTop: "1px solid rgba(70, 116, 217, 0.2)",
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={{ xs: 0.7, md: 1 }}>
                          <Box
                            sx={{
                              width: { xs: "18px", md: "24px" },
                              height: { xs: "18px", md: "24px" },
                              borderRadius: { xs: "4px", md: "6px" },
                              background: "linear-gradient(135deg, #4674D9 0%, #3567D6 100%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: { xs: "10px", md: "12px" },
                            }}
                          >
                            🤖
                          </Box>
                          <Box flex={1}>
                            <Typography
                              sx={{
                                color: "#282D4D",
                                fontSize: { xs: "7px", md: "8px" },
                                fontWeight: 600,
                                fontFamily: "'Poppins', sans-serif",
                                mb: { xs: 0.1, md: 0.2 },
                              }}
                            >
                              AI Risk Analysis
                            </Typography>
                            <Typography
                              sx={{
                                color: "#4C4C4C",
                                fontSize: { xs: "6px", md: "7px" },
                                fontFamily: "'Poppins', sans-serif",
                              }}
                            >
                              Low risk • No immediate action needed
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Right Column - Feature Cards */}
            <Grid 
              item 
              xs={12} 
              md={4} 
              sx={{ 
                display: "flex", 
                flexDirection: "column",
                pl: { xs: 0, md: 2 },
              }}
            >
              <Stack spacing={{ xs: 2.5, md: 3 }} sx={{ width: "100%", height: "100%" }}>
                <FeatureCard {...servicesData.features[2]} />
                <FeatureCard 
                  icon="BrainCircuit"
                  title={servicesData.riskAnalysis.title}
                  description={servicesData.riskAnalysis.description}
                  details={servicesData.riskAnalysis.features}
                />
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </ServicesContainer>
  );
};

export default ServicesSection;